import {
  proposeDropsetScheme,
  type DropsetStepDraft,
  type DropsetVariant,
  parseLocaleNumber,
} from '@heraion/shared';
import type { DraftSet } from './state/loggerDraft';

// The subset of a DraftSet the dropset patches touch.
export type DropsetPatch = Partial<
  Pick<DraftSet, 'reps' | 'weight' | 'dropset'>
>;

// One raw (string-valued) round of a dropset/myorep chain, in display
// units. `extraNegative` only applies to bodyweight tops carried over
// from the parent row; chain steps are always positive (no per-step sign
// toggle in the UI).
export type RawDropsetRound = {
  reps: string;
  weight: string;
  extraNegative: boolean;
  completedAt: string | null;
};

// Ordered raw rounds (top first) for a draft set that carries a dropset
// chain, applying the promote rule shared by save (workoutPayload) and
// live totals (totalsMath):
//   • new model — the parent reps/weight are a derived summary (blank),
//     and EVERY round including the top lives in `dropset.steps`, so the
//     rounds are the steps as-is (steps[0] = top).
//   • back-compat — a draft that still carries a typed parent (an
//     in-flight draft persisted before the all-rounds-in-steps change,
//     or a set converted with a value already in the row) treats the
//     parent as the top round and ALL steps as drops.
// Keeping the promote decision in one place stops save and totals from
// disagreeing on which round is the top (which would mis-derive the
// persisted parent set and the e1RM/PR comparator).
export const draftDropsetRounds = (s: DraftSet): RawDropsetRound[] => {
  const chain = s.dropset;
  if (!chain) return [];
  const stepRounds: RawDropsetRound[] = chain.steps.map((st) => ({
    reps: st.reps,
    weight: st.weight,
    extraNegative: false,
    completedAt: st.completedAt ?? null,
  }));
  // A positive parent rep count means a real top set sits in the parent
  // row (old/back-compat shape). A blank or 0 parent → new model, where
  // every round lives in the chain and the parent is just a summary.
  const parentReps = parseInt(s.reps, 10);
  const parentTyped = Number.isFinite(parentReps) && parentReps > 0;
  if (parentTyped) {
    return [
      {
        reps: s.reps,
        weight: s.weight,
        extraNegative: s.extraNegative,
        completedAt: s.completedAt,
      },
      ...stepRounds,
    ];
  }
  return stepRounds;
};

const toStepDraft = (r: {
  reps: number | null;
  weight: number | null;
}): DropsetStepDraft => ({
  reps: r.reps !== null ? String(r.reps) : '',
  weight: r.weight !== null ? String(r.weight) : '',
  completedAt: null,
});

// Patch to activate / convert a set to a dropset or myorep chain.
//   • fresh activation — seed the chain from the working set via
//     proposeDropsetScheme and CLEAR the parent reps/weight, so the
//     parent row becomes a derived summary and every round (top
//     included) lives in the chain.
//   • variant swap on an existing chain — re-seed the rounds for the NEW
//     technique from the top round, so a classical→myorep swap doesn't
//     keep the classical drop weights (returns null when the variant is
//     unchanged so the caller can skip a no-op dispatch).
export const dropsetVariantPatch = (
  s: DraftSet,
  variant: DropsetVariant,
): DropsetPatch | null => {
  if (s.dropset) {
    if (s.dropset.variant === variant) return null;
    // Re-seed the chain for the new technique instead of relabelling only.
    // Relabelling kept the previous variant's step weights, so a
    // Dropset→Myo-rep swap left the 80%/60% drop weights on a myorep
    // chain, where every cluster should read "same load" (blank). We
    // re-derive from the top round (steps[0]) through the same seeder as a
    // fresh activation, so a swap behaves like re-picking the technique.
    // (inbox 6ea50c1f)
    return { dropset: { variant, steps: seedSteps(s.dropset.steps[0], variant) } };
  }
  return {
    reps: '',
    weight: '',
    dropset: { variant, steps: seedSteps(topFromRow(s), variant) },
  };
};

// Parse a raw (string) top round into the numeric top the seeder needs,
// then map the proposed rounds into draft steps. Shared by fresh
// activation (top = the working row) and variant swap (top = steps[0]).
const seedSteps = (
  top: { reps: string; weight: string } | undefined,
  variant: DropsetVariant,
): DropsetStepDraft[] => {
  const parsedReps = top ? parseInt(top.reps, 10) : NaN;
  const parsedWeight = top ? parseLocaleNumber(top.weight) : NaN;
  return proposeDropsetScheme({
    variant,
    topReps: Number.isFinite(parsedReps) ? parsedReps : null,
    topWeight: Number.isFinite(parsedWeight) ? parsedWeight : null,
  }).map(toStepDraft);
};

const topFromRow = (s: DraftSet) => ({ reps: s.reps, weight: s.weight });

// Patch to remove a chain: hand the top round's reps/weight back to the
// parent row and clear the chain, so the set returns to a plain working
// set carrying its top values rather than going blank.
export const removeDropsetPatch = (s: DraftSet): DropsetPatch => {
  const top = s.dropset?.steps[0];
  return {
    reps: top?.reps ?? '',
    weight: top?.weight ?? '',
    dropset: null,
  };
};

// #213: the «Assisted» chip flips the sign flag of the bound set's load.
export const toggleAssistedPatch = (
  s: DraftSet,
): Pick<DraftSet, 'extraNegative'> => ({ extraNegative: !s.extraNegative });

// #41: the «Timed» chip. On, the set stops being a rep count: the reps and
// the AMRAP outcome go, and an assist does not become an added load. Off,
// only the flag goes: a typed duration stays as the set's measured time.
export const toggleTimedPatch = (
  s: DraftSet,
): Partial<Pick<DraftSet, 'timed' | 'reps' | 'amrap' | 'weight' | 'extraNegative'>> =>
  s.timed === true
    ? { timed: false }
    : {
        timed: true,
        reps: '',
        amrap: false,
        ...(s.extraNegative ? { weight: '', extraNegative: false } : {}),
      };
